import Enum from '/@/modules/core/app/helpers/Enum';
import Lang from '/@/modules/core/app/services/Lang';
import { EstateFormKey } from '/@/modules/estate/constants/EstateFormKey';
import { PredefinedJurisdiction } from '/@/modules/predefined/enums/PredefinedJurisdiction';
import { PredefinedCountry } from '/@/modules/predefined/enums/PredefinedCountry';
import { EsignDocumentForm } from '@/modules/esign/constants/EsignDocumentForm';

export class EstateFormType extends Enum {
  key!: string;
  value!: string;
  esignTemplateKey!: number | string;
  displayType!: string | null;
  columnDisplayText?: string;
  minCopies?: number;
  maxCopies?: number;
  copyEditText?: string;

  static DE_157: EstateFormType = {
    key: EstateFormKey.DE_157,
    get value(): string {
      return Lang.t('estate.notice-administration-creditors');
    },
    displayType: 'DE-157',
    esignTemplateKey: EsignDocumentForm.FORM_401697
  };

  static DIRECTIONS_AND_AUTHORIZATION: EstateFormType = {
    key: EstateFormKey.DIRECTIONS_AND_AUTHORIZATION,
    get value(): string {
      return Lang.t('estate.directions-authorization-and');
    },
    displayType: null,
    esignTemplateKey: EsignDocumentForm.FORM_551851
  };

  static ISP_1200: EstateFormType = {
    key: EstateFormKey.ISP_1200,
    get value(): string {
      return Lang.t('estate.cpp-death-benefits');
    },
    displayType: 'ISP 1200',
    esignTemplateKey: EsignDocumentForm.FORM_573471
  };

  static ISP_1300: EstateFormType = {
    key: EstateFormKey.ISP_1300,
    get value(): string {
      return Lang.t('estate.survivor-pension-children');
    },
    displayType: 'ISP 1300',
    esignTemplateKey: EsignDocumentForm.FORM_287403
  };

  static ISP_1201: EstateFormType = {
    key: EstateFormKey.ISP_1201,
    get value(): string {
      return Lang.t('estate.old-age-security');
    },
    displayType: 'ISP 1201',
    esignTemplateKey: EsignDocumentForm.FORM_859822
  };

  static ISP_3104: EstateFormType = {
    key: EstateFormKey.ISP_3104,
    get value(): string {
      return Lang.t('estate.statutory-declaration-union');
    },
    displayType: 'ISP 3104',
    esignTemplateKey: EsignDocumentForm.FORM_701554
  };

  static NOTICE_OF_DEATH_TO_CRA: EstateFormType = {
    key: EstateFormKey.NOTICE_OF_DEATH_TO_CRA,
    get value(): string {
      return Lang.t('estate.notice-death-cra');
    },
    displayType: null,
    esignTemplateKey: EsignDocumentForm.FORM_918855
  };

  static NOTICE_OF_DEATH_TO_EQUIFAX: EstateFormType = {
    key: EstateFormKey.NOTICE_OF_DEATH_TO_EQUIFAX,
    get value(): string {
      return Lang.t('estate.notice-death-equifax');
    },
    displayType: null,
    esignTemplateKey: EsignDocumentForm.FORM_625776
  };

  static NOTICE_OF_DEATH_TO_TRANSUNION: EstateFormType = {
    key: EstateFormKey.NOTICE_OF_DEATH_TO_TRANSUNION,
    get value(): string {
      return Lang.t('estate.notice-death-transunion');
    },
    displayType: null,
    esignTemplateKey: EsignDocumentForm.FORM_384607
  };

  static NOTICE_TO_DEPARTMENT_OF_HEALTH_CARE_SERVICES: EstateFormType = {
    key: EstateFormKey.NOTICE_TO_DEPARTMENT_OF_HEALTH_CARE_SERVICES,
    get value(): string {
      return Lang.t('estate.department-health-care');
    },
    displayType: null,
    esignTemplateKey: EsignDocumentForm.FORM_478088
  };

  static NOTICE_OF_DEATH_TO_EXPERIAN: EstateFormType = {
    key: EstateFormKey.NOTICE_OF_DEATH_TO_EXPERIAN,
    get value(): string {
      return Lang.t('estate.notice-death-experian');
    },
    displayType: null,
    esignTemplateKey: EsignDocumentForm.FORM_302708
  };

  static NOTICE_TO_FRANCHISE_TAX_BOARD: EstateFormType = {
    key: EstateFormKey.NOTICE_TO_FRANCHISE_TAX_BOARD,
    get value(): string {
      return Lang.t('estate.franchise-tax-board');
    },
    displayType: null,
    esignTemplateKey: EsignDocumentForm.FORM_967195
  };

  static NOTICE_TO_VICTIMS_COMPENSATION_BOARD: EstateFormType = {
    key: EstateFormKey.NOTICE_TO_VICTIMS_COMPENSATION_BOARD,
    get value(): string {
      return Lang.t('estate.victims-compensation-board');
    },
    displayType: null,
    esignTemplateKey: EsignDocumentForm.FORM_381593
  };

  static TAX_AUTHORIZATION_FORM: EstateFormType = {
    key: EstateFormKey.TAX_AUTHORIZATION_FORM,
    get value(): string {
      return Lang.t('estate.cra-authorization-request');
    },
    displayType: null,
    esignTemplateKey: EsignDocumentForm.FORM_209413
  };

  static FORM_56_NOTICE_CONCERNING_FIDUCIARY_RESPONSIBILITY: EstateFormType = {
    key: EstateFormKey.FORM_56_NOTICE_CONCERNING_FIDUCIARY_RESPONSIBILITY,
    get value(): string {
      return Lang.t('estate.notice-concerning-responsibility');
    },
    displayType: 'Form-56',
    esignTemplateKey: EsignDocumentForm.FORM_569844
  };

  static DE_160: EstateFormType = {
    key: EstateFormKey.DE_160,
    get value(): string {
      return Lang.t('estate.inventory-appraisal-and');
    },
    get displayType(): string {
      return Lang.t('estate.de');
    },
    esignTemplateKey: EsignDocumentForm.FORM_788121
  };

  static DE_161_ATTACHMENT_1: EstateFormType = {
    key: EstateFormKey.DE_161_ATTACHMENT_1,
    get value(): string {
      return Lang.t('estate.inventory-appraisal-and');
    },
    get displayType(): string {
      return Lang.t('estate.de-attachment-no');
    },
    esignTemplateKey: EsignDocumentForm.FORM_463640,
    get columnDisplayText(): string {
      return Lang.t('estate.attachment');
    }
  };

  static DE_161_ATTACHMENT_2: EstateFormType = {
    key: EstateFormKey.DE_161_ATTACHMENT_2,
    get value(): string {
      return Lang.t('estate.inventory-appraisal-and');
    },
    get displayType(): string {
      return Lang.t('estate.505fc47f-adfe-4aa3-9f79-0e126626ec38');
    },
    esignTemplateKey: EsignDocumentForm.FORM_871042,
    get columnDisplayText(): string {
      return Lang.t('estate.ff26f212-5dcf-481c-abe4-51c23407e587');
    }
  };

  static DE_161_ATTACHMENT_1_V2: EstateFormType = {
    key: EstateFormKey.DE_161_ATTACHMENT_1_V2,
    get value(): string {
      return Lang.t('estate.inventory-appraisal-and');
    },
    get displayType(): string {
      return Lang.t('estate.de-attachment-no');
    },
    esignTemplateKey: EsignDocumentForm.FORM_902309,
    get columnDisplayText(): string {
      return Lang.t('estate.attachment');
    }
  };

  static DE_161_ATTACHMENT_2_V2: EstateFormType = {
    key: EstateFormKey.DE_161_ATTACHMENT_2_V2,
    get value(): string {
      return Lang.t('estate.inventory-appraisal-and');
    },
    get displayType(): string {
      return Lang.t('estate.505fc47f-adfe-4aa3-9f79-0e126626ec38');
    },
    esignTemplateKey: EsignDocumentForm.FORM_337290,
    get columnDisplayText(): string {
      return Lang.t('estate.ff26f212-5dcf-481c-abe4-51c23407e587');
    }
  };

  static MC_025: EstateFormType = {
    key: EstateFormKey.MC_025,
    get value(): string {
      return Lang.t('esign.judicial-council-form');
    },
    displayType: 'MC-025',
    esignTemplateKey: EsignDocumentForm.FORM_147987,
    minCopies: 0,
    maxCopies: 5,
    get copyEditText(): string {
      return Lang.t('esign.select-after-de111');
    }
  };

  static COVER_LETTER: EstateFormType = {
    key: EstateFormKey.COVER_LETTER,
    get value(): string {
      return Lang.t('document.cover-inventory-appraisal');
    },
    displayType: null,
    esignTemplateKey: ''
  };

  static getForms(jurisdictionId: number): EstateFormType[] {
    const jurisdiction: PredefinedJurisdiction | undefined = PredefinedJurisdiction.getByKey(
      jurisdictionId,
      'id'
    );

    if (!jurisdiction) {
      return [];
    }

    switch (jurisdiction.country.id) {
      case PredefinedCountry.CANADA.id:
        if (jurisdiction.id === PredefinedJurisdiction.QUEBEC.id) {
          return [];
        }

        return [
          EstateFormType.DIRECTIONS_AND_AUTHORIZATION,
          EstateFormType.ISP_1200,
          EstateFormType.ISP_1300,
          EstateFormType.ISP_1201,
          EstateFormType.ISP_3104,
          EstateFormType.NOTICE_OF_DEATH_TO_CRA,
          EstateFormType.NOTICE_OF_DEATH_TO_EQUIFAX,
          EstateFormType.NOTICE_OF_DEATH_TO_TRANSUNION,
          EstateFormType.TAX_AUTHORIZATION_FORM
        ];
      case PredefinedCountry.UNITED_STATES.id:
        return EstateFormType.getAmericanJurisdictionForms(jurisdiction.id);
      default:
        return [];
    }
  }

  static getAmericanJurisdictionForms(jurisdictionId: number): EstateFormType[] {
    const federalForms: EstateFormType[] = [
      EstateFormType.NOTICE_OF_DEATH_TO_TRANSUNION,
      EstateFormType.NOTICE_OF_DEATH_TO_EQUIFAX,
      EstateFormType.NOTICE_OF_DEATH_TO_EXPERIAN,
      EstateFormType.FORM_56_NOTICE_CONCERNING_FIDUCIARY_RESPONSIBILITY
    ];

    switch (jurisdictionId) {
      case PredefinedJurisdiction.CALIFORNIA.id:
        return [
          ...federalForms,
          EstateFormType.NOTICE_TO_FRANCHISE_TAX_BOARD,
          EstateFormType.NOTICE_TO_DEPARTMENT_OF_HEALTH_CARE_SERVICES,
          EstateFormType.NOTICE_TO_VICTIMS_COMPENSATION_BOARD,
          EstateFormType.DE_157,
          EstateFormType.DE_160,
          EstateFormType.DE_161_ATTACHMENT_1,
          EstateFormType.DE_161_ATTACHMENT_2,
          EstateFormType.MC_025
        ];
      default:
        return federalForms;
    }
  }
}
